/**
 * Toolbar Store – 4-stage height-based resizable floating toolbar.
 * Pattern matches mainNavStore.ts (literal stages, continuous height, localStorage).
 *
 * Stages control the visual density / subtlety of the toolbar:
 *   tiny:   14px – ultra-discreet thin bar
 *   small:  24px – compact icons
 *   medium: 32px – normal (default)
 *   full:   44px – spacious, larger icons
 */

import { createStore } from '@xstate/store';

export type ToolbarStage = 'tiny' | 'small' | 'medium' | 'full';

export const TOOLBAR_STAGE_HEIGHTS: Record<ToolbarStage, number> = {
  tiny: 14,
  small: 24,
  medium: 32,
  full: 44,
};

export const TOOLBAR_STAGES_ORDERED: ToolbarStage[] = ['tiny', 'small', 'medium', 'full'];

/** Derive rendering stage from continuous height (simple thresholds). */
export function computeToolbarStage(height: number): ToolbarStage {
  if (height >= 38) return 'full';
  if (height >= 28) return 'medium';
  if (height >= 18) return 'small';
  return 'tiny';
}

const HEIGHT_STORAGE_KEY = 'roadmap_2026_toolbar_height';

function loadHeight(): number {
  try {
    const stored = localStorage.getItem(HEIGHT_STORAGE_KEY);
    if (stored) {
      const val = Number(stored);
      if (val >= TOOLBAR_STAGE_HEIGHTS['tiny'] && val <= TOOLBAR_STAGE_HEIGHTS['full']) return val;
    }
  } catch {}
  return TOOLBAR_STAGE_HEIGHTS['medium'];
}

const initialHeight = loadHeight();

export const toolbarStore = createStore({
  context: {
    stage: computeToolbarStage(initialHeight) as ToolbarStage,
    rawHeight: initialHeight,
    isDragging: false,
  },
  on: {
    'toolbar.setStage': (_ctx, { stage }: { stage: ToolbarStage }) => ({
      stage,
      rawHeight: TOOLBAR_STAGE_HEIGHTS[stage],
      isDragging: false,
    }),
    'toolbar.setRawHeight': (ctx, { height }: { height: number }) => ({
      ...ctx,
      rawHeight: height,
      stage: computeToolbarStage(height) as ToolbarStage,
    }),
    'toolbar.startDrag': (ctx) => ({ ...ctx, isDragging: true }),
    'toolbar.endDrag': (ctx) => ({ ...ctx, isDragging: false }),
  },
});

// Persist height to localStorage
toolbarStore.subscribe((snapshot) => {
  if (!snapshot.context.isDragging) {
    try { localStorage.setItem(HEIGHT_STORAGE_KEY, String(snapshot.context.rawHeight)); } catch {}
  }
});
